import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { formatCurrency } from '../../utils/calculations';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';

const SpecialOffers = ({ customerProfile, restaurantInfo, orderHistory, onApplyOffer }) => {
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(false);
    const restaurantId = localStorage.getItem('restaurantId');

    useEffect(() => {
        if (restaurantId) {
            fetchActiveOffers();
        }
    }, [restaurantId]);

    const fetchActiveOffers = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_ENDPOINTS.OFFERS}/active/${restaurantId}`);
            setOffers(response.data);
        } catch (error) {
            console.error('Error fetching offers:', error);
            // Fallback to generated offers if API fails
            generateOffers();
        } finally {
            setLoading(false);
        }
    };

    const generateOffers = () => {
        const generatedOffers = [];
        
        // Loyalty points offer
        if (customerProfile?.loyaltyPoints >= 100) {
            generatedOffers.push({
                id: 'loyalty-discount',
                title: '🎉 Loyalty Reward!',
                description: `Use ${customerProfile.loyaltyPoints} points for ${formatCurrency(customerProfile.loyaltyPoints)} off your next order!`,
                type: 'loyalty',
                value: customerProfile.loyaltyPoints,
                color: 'from-purple-500 to-pink-500'
            });
        }

        // First time customer offer
        if (!orderHistory || orderHistory.length === 0) {
            generatedOffers.push({
                id: 'first-time',
                title: '🎊 Welcome Offer!',
                description: 'Get 10% off on your first order! Minimum order value ₹200',
                type: 'percentage',
                value: 10,
                minOrder: 200,
                color: 'from-green-500 to-blue-500'
            });
        }

        // Frequent customer offer
        if (orderHistory && orderHistory.length >= 5) {
            generatedOffers.push({
                id: 'frequent-customer',
                title: '⭐ VIP Customer!',
                description: 'You\'ve ordered 5+ times! Enjoy free delivery on orders above ₹300',
                type: 'free-delivery',
                minOrder: 300,
                color: 'from-yellow-500 to-orange-500'
            });
        }

        // Weekend special
        const today = new Date();
        if (today.getDay() === 0 || today.getDay() === 6) {
            generatedOffers.push({
                id: 'weekend-special',
                title: '🎈 Weekend Special!',
                description: 'Buy 2 Get 1 Free on selected appetizers this weekend!',
                type: 'bogo',
                color: 'from-red-500 to-pink-500'
            });
        }

        // High spender offer
        if (customerProfile?.totalSpent >= 2000) {
            generatedOffers.push({
                id: 'high-spender',
                title: '💎 Premium Customer!',
                description: 'Enjoy 15% off on orders above ₹500. You\'ve spent over ₹2000 with us!',
                type: 'percentage',
                value: 15,
                minOrder: 500,
                color: 'from-indigo-500 to-purple-500'
            });
        }

        // Birthday offer (mock - would need actual birthday data)
        const isBirthdayMonth = Math.random() > 0.9; // 10% chance for demo
        if (isBirthdayMonth) {
            generatedOffers.push({
                id: 'birthday',
                title: '🎂 Birthday Special!',
                description: 'Happy Birthday! Enjoy a complimentary dessert with any main course order!',
                type: 'free-item',
                color: 'from-pink-500 to-red-500'
            });
        }

        setOffers(generatedOffers);
    };

    const getOfferIcon = (offer) => {
        if (offer.discountType) {
            return offer.discountType === 'percentage' ? '💰' : '🎁';
        }
        // Fallback for generated offers
        switch (offer.type) {
            case 'loyalty': return '🏆';
            case 'percentage': return '💰';
            case 'free-delivery': return '🚚';
            case 'bogo': return '🎁';
            case 'free-item': return '🍰';
            default: return '🎉';
        }
    };

    const handleApplyOffer = async (offer) => {
        if (onApplyOffer) {
            onApplyOffer(offer);
        }
    };

    const formatOfferValue = (offer) => {
        if (offer.discountType === 'percentage') {
            return `${offer.discountValue}% OFF`;
        } else if (offer.discountType === 'fixed') {
            return `₹${offer.discountValue} OFF`;
        }
        return offer.value ? `${offer.value}% OFF` : 'Special Offer';
    };

    const getOfferColor = (offer, index) => {
        if (offer.color) return offer.color;
        
        const colors = [
            'from-blue-500 to-purple-600',
            'from-green-500 to-blue-500',
            'from-purple-500 to-pink-500',
            'from-yellow-500 to-orange-500',
            'from-red-500 to-pink-500',
            'from-indigo-500 to-purple-500'
        ];
        return colors[index % colors.length];
    };

    if (offers.length === 0) {
        return null;
    }

    return (
        <motion.div 
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <div className="flex items-center space-x-3 mb-6">
                <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                    Special Offers
                </h2>
                <div className="flex-1 h-px bg-gradient-to-r from-gray-300 to-transparent"></div>
                <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    {offers.length} available
                </span>
            </div>

            {loading ? (
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {offers.map((offer, index) => {
                        const minOrder = offer.minOrderAmount || offer.minOrder;
                        const validUntil = offer.validUntil ? new Date(offer.validUntil).toLocaleDateString() : null;
                        
                        return (
                            <motion.div
                                key={offer._id || offer.id}
                                className={`bg-gradient-to-r ${getOfferColor(offer, index)} rounded-2xl p-6 text-white shadow-xl hover:shadow-2xl transition-all duration-300`}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                whileHover={{ scale: 1.05, y: -5 }}
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="text-4xl">{getOfferIcon(offer)}</div>
                                    <div className="bg-white/20 px-3 py-1 rounded-full text-sm font-semibold">
                                        {validUntil ? `Until ${validUntil}` : 'Limited Time'}
                                    </div>
                                </div>
                                
                                <h3 className="text-xl font-bold mb-2">{offer.title}</h3>
                                <p className="text-white/90 text-sm mb-4">{offer.description}</p>
                                
                                <div className="bg-white/20 rounded-lg p-3 mb-4">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <p className="text-xs text-white/80">Discount</p>
                                            <p className="font-bold">{formatOfferValue(offer)}</p>
                                        </div>
                                        {minOrder > 0 && (
                                            <div className="text-right">
                                                <p className="text-xs text-white/80">Min Order</p>
                                                <p className="font-bold">{formatCurrency(minOrder)}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                <motion.button
                                    onClick={() => handleApplyOffer(offer)}
                                    className="w-full bg-white/20 hover:bg-white/30 text-white font-bold py-3 px-4 rounded-xl transition-all duration-300 backdrop-blur-sm"
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    Apply Offer
                                </motion.button>
                            </motion.div>
                        );
                    })}
                </div>
            )}

            {/* Loyalty Points Display */}
            {customerProfile?.loyaltyPoints > 0 && (
                <motion.div 
                    className="mt-8 bg-gradient-to-r from-amber-400 to-orange-500 rounded-2xl p-6 text-white"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-2xl font-bold mb-2">💎 Your Loyalty Points</h3>
                            <p className="text-white/90">
                                You have <span className="font-bold text-3xl">{customerProfile.loyaltyPoints}</span> points
                            </p>
                            <p className="text-white/80 text-sm mt-1">
                                Earn 1 point for every ₹10 spent • Redeem points for discounts
                            </p>
                        </div>
                        <div className="text-6xl">🏆</div>
                    </div>
                    
                    <div className="mt-4 bg-white/20 rounded-xl p-4">
                        <div className="flex justify-between items-center">
                            <span className="text-white/90">Points Value:</span>
                            <span className="font-bold text-xl">{formatCurrency(customerProfile.loyaltyPoints)}</span>
                        </div>
                    </div>
                </motion.div>
            )}
        </motion.div>
    );
};

export default SpecialOffers;